import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon, XCircleIcon, ClockIcon,
  EyeIcon, XMarkIcon, BookOpenIcon, PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .am*{box-sizing:border-box;}
  .am{font-family:'Sora',sans-serif;color:#1a1d2e;animation:amFade .4s ease both;}
  @keyframes amFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .am-header{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem;flex-wrap:wrap;gap:1rem;}
  .am-title{font-size:1.35rem;font-weight:700;letter-spacing:-.02em;color:#0f1123;}
  .am-sub{font-size:.76rem;color:#9094a8;margin-top:3px;}
  .req-banner{background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:10px 14px;font-size:.76rem;color:#c2410c;margin-bottom:1.2rem;display:flex;align-items:center;gap:8px;}
  .stat-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:.9rem;margin-bottom:1.4rem;}
  .stat-card{background:white;border-radius:14px;padding:1rem 1.1rem;border:1px solid #e8ebf4;cursor:pointer;transition:all .15s;}
  .stat-card:hover{border-color:#f97316;transform:translateY(-1px);}
  .stat-card.active-filter{border-color:#f97316;background:#fff7ed;}
  .s-icon{width:32px;height:32px;border-radius:9px;display:flex;align-items:center;justify-content:center;margin-bottom:.6rem;}
  .s-icon svg{width:15px;height:15px;stroke-width:1.8;}
  .s-icon.amber{background:#fffbeb;color:#f59e0b;} .s-icon.green{background:#ecfdf5;color:#10c98e;}
  .s-icon.red{background:#fef2f2;color:#ef4444;}   .s-icon.blue{background:#eef2ff;color:#3b6ef8;}
  .s-label{font-size:.67rem;font-weight:600;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .s-value{font-size:1.4rem;font-weight:700;color:#0f1123;font-family:'JetBrains Mono',monospace;}
  .btn{display:inline-flex;align-items:center;gap:7px;padding:8px 14px;border:none;border-radius:9px;font-family:'Sora',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;transition:all .18s;}
  .btn svg{width:14px;height:14px;stroke-width:2;}
  .btn-orange{background:#f97316;color:white;} .btn-orange:hover{box-shadow:0 4px 14px rgba(249,115,22,.3);}
  .btn-red{background:#fef2f2;color:#ef4444;border:1px solid #fecaca;} .btn-red:hover{background:#ef4444;color:white;}
  .btn-ghost{background:#f0f2f7;color:#6b7194;} .btn-ghost:hover{background:#e4e7f0;}
  .btn:disabled{opacity:.5;cursor:not-allowed;}
  .table-panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;}
  .panel-head{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid #f0f2f8;}
  .panel-title{font-size:.86rem;font-weight:600;color:#1a1d2e;}
  .mat-table{width:100%;border-collapse:collapse;}
  .mat-table thead{background:#fafbff;border-bottom:1px solid #edf0f9;}
  .mat-table th{padding:10px 16px;text-align:center !important;font-size:.65rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .mat-table tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .mat-table tbody tr:last-child{border-bottom:none;} .mat-table tbody tr:hover{background:#fafbff;}
  .mat-table td{ padding:11px 16px;font-size:.8rem;vertical-align:middle; text-align:center !important; }
  .mat-cards{display:none;padding:10px;}
  .mat-card{background:#fafbff;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
  .mc-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;gap:8px;}
  .mc-title{font-size:.86rem;font-weight:700;color:#0f1123;}
  .mc-desc{font-size:.72rem;color:#9094a8;margin-top:2px;}
  .mc-row{display:flex;justify-content:space-between;align-items:center;font-size:.76rem;padding:4px 0;border-bottom:1px solid #f0f2f8;gap:8px;}
  .mc-row:last-of-type{border-bottom:none;}
  .mc-label{color:#9094a8;font-weight:600;white-space:nowrap;}
  .mc-actions{display:flex;gap:6px;margin-top:8px;flex-wrap:wrap;}
  .type-chip{display:inline-flex;font-size:.65rem;font-weight:700;padding:2px 8px;border-radius:6px;text-transform:uppercase;}
  .type-chip.pdf{background:#fee2e2;color:#ef4444;} .type-chip.doc{background:#dbeafe;color:#3b6ef8;}
  .type-chip.ppt{background:#fef3c7;color:#f59e0b;} .type-chip.image{background:#d1fae5;color:#10c98e;}
  .type-chip.video{background:#ede9fe;color:#8b5cf6;} .type-chip.youtube{background:#fee2e2;color:#ef4444;}
  .type-chip.link{background:#e0f2fe;color:#0284c7;} .type-chip.other{background:#f3f4f6;color:#6b7280;}
  .status-badge{display:inline-flex;align-items:center;gap:4px;font-size:.67rem;font-weight:600;padding:3px 9px;border-radius:99px;}
  .status-badge svg{width:11px;height:11px;stroke-width:2.5;}
  .status-badge.pending{background:#fffbeb;color:#f59e0b;}
  .status-badge.approved{background:#ecfdf5;color:#10c98e;}
  .status-badge.rejected{background:#fef2f2;color:#ef4444;}
  .teacher-cell{display:flex;align-items:center;gap:8px;}
  .t-av{width:28px;height:28px;border-radius:7px;background:#fff7ed;color:#f97316;display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;flex-shrink:0;}
  .action-btns{display:flex;gap:6px;align-items:center;}
  .reject-reason{font-size:.68rem;color:#ef4444;font-style:italic;margin-top:2px;max-width:180px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .am-empty{text-align:center;padding:3.5rem 2rem;}
  .am-empty-icon{width:48px;height:48px;border-radius:13px;background:#fff7ed;color:#f97316;display:flex;align-items:center;justify-content:center;margin:0 auto 12px;}
  .am-empty-icon svg{width:22px;height:22px;stroke-width:1.6;}
  .am-empty-title{font-size:.9rem;font-weight:600;color:#1a1d2e;margin-bottom:4px;}
  .am-empty-sub{font-size:.76rem;color:#9094a8;}
  .am-loading{display:flex;align-items:center;justify-content:center;height:200px;gap:10px;color:#9094a8;}
  .spinner{width:18px;height:18px;border:2px solid #e4e7f0;border-top-color:#f97316;border-radius:50%;animation:spin .7s linear infinite;}
  @keyframes spin{to{transform:rotate(360deg);}}
  .modal-backdrop{position:fixed;inset:0;background:rgba(15,17,35,.55);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;padding:26px;width:100%;max-width:400px;max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);animation:modalIn .25s ease;}
  @keyframes modalIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
  .modal-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;}
  .modal-title{font-size:1rem;font-weight:700;color:#0f1123;}
  .modal-close{width:28px;height:28px;border-radius:7px;border:none;background:#f0f2f7;cursor:pointer;display:flex;align-items:center;justify-content:center;}
  .modal-close svg{width:14px;height:14px;stroke-width:2;}
  .modal-btns{display:flex;gap:8px;justify-content:flex-end;margin-top:16px;flex-wrap:wrap;}
  .form-label{font-size:.68rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;display:block;margin-bottom:6px;}
  .form-textarea{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 11px;border:1.5px solid #e0e4f0;border-radius:9px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;resize:vertical;min-height:80px;}
  .form-textarea:focus{border-color:#f97316;background:white;}
  @media(max-width:900px){.stat-grid{grid-template-columns:repeat(2,1fr);}}
  @media(max-width:768px){
    .am-title{font-size:1.1rem;}
    .mat-table-wrap{display:none!important;} .mat-cards{display:block;}
    .modal{padding:18px;} .modal-btns{justify-content:stretch;} .modal-btns .btn{flex:1;justify-content:center;}
  }
  @media(max-width:480px){.stat-grid{grid-template-columns:1fr 1fr;}}
`;

const ini = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short',year:'numeric'});
const fmtSize = (b) => b ? (b>1024*1024?`${(b/1024/1024).toFixed(1)}MB`:`${(b/1024).toFixed(0)}KB`) : null;
const getStatus = (m) => m.approved ? 'approved' : m.rejectionReason ? 'rejected' : 'pending';

const CoordMaterials = () => {
  const [materials,  setMaterials]  = useState([]);
  const [stats,      setStats]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [modal,      setModal]      = useState(null); // {action:'approve'|'reject', material}
  const [reason,     setReason]     = useState('');
  const [saving,     setSaving]     = useState(false);

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchMaterials(); }, [filter]);

  const fetchStats = async () => {
    try { const {data} = await api.get('/coordinator/materials/stats'); setStats(data); } catch {}
  };

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const {data} = await api.get(`/coordinator/materials${params}`);
      setMaterials(data);
    } catch { toast.error('Failed to load materials'); }
    finally { setLoading(false); }
  };

  const openModal = (action, material) => {
    setModal({ action, material });
    setReason('');
  };

  const submit = async () => {
    if (modal.action === 'reject' && !reason.trim()) {
      toast.error('Reason is required'); return;
    }
    try {
      setSaving(true);
      const { action, material } = modal;
      await api.post('/coordinator/requests', {
        type: 'material_action',
        payload: {
          action,
          materialId:  material._id,
          title:       material.title,
          teacherName: material.uploadedBy?.name,
          reason:      reason || undefined,
        },
      });
      toast.success('Request submitted — awaiting admin approval');
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const ActionButtons = ({ m }) => {
    const status = getStatus(m);
    return (
      <div className="action-btns">
        {(m.fileUrl || m.youtubeUrl) && (
          <a href={m.fileUrl || m.youtubeUrl} target="_blank" rel="noreferrer"
            className="btn btn-ghost" style={{padding:'5px 10px'}} title="View">
            <EyeIcon />
          </a>
        )}
        {status !== 'approved' && (
          <button className="btn btn-orange" style={{padding:'5px 10px'}}
            onClick={() => openModal('approve', m)} title="Request: Approve">
            <CheckCircleIcon />
          </button>
        )}
        {status !== 'rejected' && (
          <button className="btn btn-red" style={{padding:'5px 10px'}}
            onClick={() => openModal('reject', m)} title="Request: Reject">
            <XCircleIcon />
          </button>
        )}
      </div>
    );
  };

  const FILTERS = [
    { key:'all',      label:'Total',          icon:<BookOpenIcon />,    color:'blue'  },
    { key:'approved', label:'Approved',       icon:<CheckCircleIcon />, color:'green' },
    { key:'pending',  label:'Pending Review', icon:<ClockIcon />,       color:'amber' },
    { key:'rejected', label:'Rejected',       icon:<XCircleIcon />,     color:'red'   },
  ];

  return (
    <div className="am"><style>{style}</style>
      <div className="am-header">
        <div><div className="am-title">Materials</div><div className="am-sub">Review teacher-uploaded learning materials · submit requests to approve or reject</div></div>
      </div>
      <div className="req-banner">📋 Approve and reject actions require admin approval before taking effect.</div>

      {stats && (
        <div className="stat-grid">
          {FILTERS.map(f => (
            <div key={f.key} className={`stat-card ${filter===f.key?'active-filter':''}`} onClick={() => setFilter(f.key)}>
              <div className={`s-icon ${f.color}`}>{f.icon}</div>
              <div className="s-label">{f.label}</div>
              <div className="s-value">{stats[f.key==='all'?'total':f.key] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="table-panel">
        <div className="panel-head">
          <div className="panel-title">
            {filter==='pending'?'Pending Approval':filter==='approved'?'Approved Materials':filter==='rejected'?'Rejected Materials':'All Materials'} ({materials.length})
          </div>
        </div>

        {loading ? (
          <div className="am-loading"><div className="spinner"/>Loading…</div>
        ) : materials.length === 0 ? (
          <div className="am-empty">
            <div className="am-empty-icon"><ClockIcon /></div>
            <div className="am-empty-title">{filter==='pending'?'No pending materials':'No materials found'}</div>
            <div className="am-empty-sub">{filter==='pending'?'All uploads have been reviewed.':'Nothing to show for this filter.'}</div>
          </div>
        ) : <>
          {/* Desktop table */}
          <div className="mat-table-wrap" style={{overflowX:'auto'}}>
            <table className="mat-table">
              <thead>
                <tr><th>Material</th><th>Type</th><th>Teacher</th><th>Class</th><th>Subject</th><th>Uploaded</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {materials.map(m => {
                  const status = getStatus(m);
                  return (
                    <tr key={m._id}>
                      <td>
                        <div style={{fontWeight:600,fontSize:'.82rem',color:'#0f1123'}}>{m.title}</div>
                        {m.description && <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:2,maxWidth:180,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{m.description}</div>}
                        {m.fileSize && <div style={{fontSize:'.67rem',color:'#b0b5cc',marginTop:1}}>{fmtSize(m.fileSize)}</div>}
                      </td>
                      <td><span className={`type-chip ${m.type}`}>{m.type}</span></td>
                      <td>
                        <div className="teacher-cell">
                          <div className="t-av">{ini(m.uploadedBy?.name)}</div>
                          <div>
                            <div style={{fontSize:'.8rem',fontWeight:600,color:'#0f1123'}}>{m.uploadedBy?.name}</div>
                            <div style={{fontSize:'.68rem',color:'#9094a8'}}>{m.uploadedBy?.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{fontSize:'.78rem',color:'#6b7194'}}>{m.class?.name || <span style={{color:'#c0c5dc'}}>All</span>}</td>
                      <td style={{fontSize:'.78rem',color:'#6b7194'}}>{m.subject || <span style={{color:'#c0c5dc'}}>—</span>}</td>
                      <td style={{fontSize:'.74rem',color:'#6b7194',fontFamily:"'JetBrains Mono',monospace"}}>{fmtDate(m.createdAt)}</td>
                      <td>
                        <span className={`status-badge ${status}`}>
                          {status==='pending'&&<ClockIcon />}{status==='approved'&&<CheckCircleIcon />}{status==='rejected'&&<XCircleIcon />}
                          {status}
                        </span>
                        {status==='rejected' && m.rejectionReason && <div className="reject-reason" title={m.rejectionReason}>{m.rejectionReason}</div>}
                      </td>
                      <td><ActionButtons m={m} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mat-cards">
            {materials.map(m => {
              const status = getStatus(m);
              return (
                <div key={m._id} className="mat-card">
                  <div className="mc-top">
                    <div>
                      <div className="mc-title">{m.title}</div>
                      {m.description && <div className="mc-desc">{m.description}</div>}
                    </div>
                    <span className={`status-badge ${status}`}>
                      {status==='pending'&&<ClockIcon />}{status==='approved'&&<CheckCircleIcon />}{status==='rejected'&&<XCircleIcon />}
                      {status}
                    </span>
                  </div>
                  <div className="mc-row"><span className="mc-label">Type</span><span className={`type-chip ${m.type}`}>{m.type}</span></div>
                  <div className="mc-row">
                    <span className="mc-label">Teacher</span>
                    <div className="teacher-cell"><div className="t-av">{ini(m.uploadedBy?.name)}</div><span style={{fontSize:'.78rem',fontWeight:600}}>{m.uploadedBy?.name}</span></div>
                  </div>
                  <div className="mc-row"><span className="mc-label">Class</span><span style={{fontSize:'.76rem',color:'#6b7194'}}>{m.class?.name||'All'}</span></div>
                  {m.subject && <div className="mc-row"><span className="mc-label">Subject</span><span style={{fontSize:'.76rem',color:'#6b7194'}}>{m.subject}</span></div>}
                  <div className="mc-row"><span className="mc-label">Uploaded</span><span style={{fontSize:'.72rem',fontFamily:"'JetBrains Mono',monospace",color:'#6b7194'}}>{fmtDate(m.createdAt)}</span></div>
                  {status==='rejected' && m.rejectionReason && <div className="mc-row"><span className="mc-label">Reason</span><span style={{fontSize:'.72rem',color:'#ef4444',fontStyle:'italic'}}>{m.rejectionReason}</span></div>}
                  <div className="mc-actions"><ActionButtons m={m} /></div>
                </div>
              );
            })}
          </div>
        </>}
      </div>

      {/* Request Modal */}
      {modal && createPortal(
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">
                Request: {modal.action==='approve'?'Approve Material':'Reject Material'}
              </div>
              <button className="modal-close" onClick={() => setModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{background:'#f8f9fc',border:'1px solid #e8ebf4',borderRadius:10,padding:12,marginBottom:14}}>
              <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{modal.material.title}</div>
              <div style={{fontSize:'.76rem',color:'#9094a8',marginTop:4}}>
                by {modal.material.uploadedBy?.name}
                {modal.material.class?.name && ` · ${modal.material.class.name}`}
                {modal.material.subject && ` · ${modal.material.subject}`}
              </div>
            </div>
            {modal.action === 'reject' && (
              <>
                <label className="form-label">Reason for rejection</label>
                <textarea className="form-textarea" required value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="e.g. Content not relevant to curriculum…"
                />
              </>
            )}
            {modal.action === 'approve' && (
              <div style={{fontSize:'.8rem',color:'#6b7194',marginBottom:8}}>
                This will request admin to mark this material as approved and visible to students.
              </div>
            )}
            <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:9,padding:'9px 12px',fontSize:'.76rem',color:'#c2410c',marginTop:8}}>
              📋 This request will be sent to admin for approval.
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={submit} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};
export default CoordMaterials;